import React, { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import AlertModal from '../components/ui/AlertModal';
import ConfirmModal from '../components/ui/ConfirmModal';
import PromptModal from '../components/ui/PromptModal';
import { AlertType } from '../components/ui/AlertModal';
import * as ReactDOMClient from 'react-dom/client';

// สร้าง context สำหรับ AlertProvider
interface AlertContextType {
  showAlert: (title: string, message: string, type?: AlertType) => void;
  showConfirm: (
    title: string, 
    message: string, 
    onConfirm: () => void, 
    options?: {
      confirmText?: string;
      cancelText?: string;
      isDanger?: boolean;
    }
  ) => void;
}

const AlertContext = createContext<AlertContextType | undefined>(undefined);

// Props สำหรับ AlertProvider
interface AlertProviderProps {
  children: ReactNode;
}

// State สำหรับ Alert
interface AlertState {
  isOpen: boolean;
  title: string;
  message: string;
  type: AlertType;
}

// State สำหรับ Confirm
interface ConfirmState {
  isOpen: boolean;
  title: string;
  message: string;
  onConfirm: () => void;
  confirmText: string;
  cancelText: string;
  isDanger: boolean;
  isLoading: boolean;
}

// AlertProvider สำหรับจัดการ Alert ทั้งหมด
export const AlertProvider: React.FC<AlertProviderProps> = ({ children }) => {
  // State สำหรับ Alert
  const [alert, setAlert] = useState<AlertState>({
    isOpen: false,
    title: '',
    message: '',
    type: 'info',
  });

  // State สำหรับ Confirm
  const [confirm, setConfirm] = useState<ConfirmState>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    confirmText: 'ยืนยัน',
    cancelText: 'ยกเลิก',
    isDanger: false,
    isLoading: false,
  });

  // แสดง Alert
  const showAlert = (title: string, message: string, type: AlertType = 'info') => {
    setAlert({
      isOpen: true,
      title,
      message,
      type,
    });
  };

  // ปิด Alert
  const closeAlert = () => {
    setAlert(prev => ({ ...prev, isOpen: false }));
  };

  // แสดง Confirm
  const showConfirm = (
    title: string, 
    message: string, 
    onConfirm: () => void, 
    options: { confirmText?: string; cancelText?: string; isDanger?: boolean } = {}
  ) => {
    const { confirmText = 'ยืนยัน', cancelText = 'ยกเลิก', isDanger = false } = options;
    
    setConfirm({
      isOpen: true,
      title,
      message,
      onConfirm,
      confirmText,
      cancelText,
      isDanger,
      isLoading: false,
    });
  };

  // ปิด Confirm
  const closeConfirm = () => {
    setConfirm(prev => ({ ...prev, isOpen: false }));
  };

  // เมื่อกดยืนยันใน Confirm
  const handleConfirm = async () => {
    try {
      // แสดงสถานะ loading
      setConfirm(prev => ({ ...prev, isLoading: true }));
      
      // เรียกฟังก์ชัน callback
      await confirm.onConfirm();
      
      // ปิด confirm dialog
      closeConfirm();
    } catch (error) {
      console.error('Error in confirm action:', error);
      // แสดง error ถ้าเกิดข้อผิดพลาด — ข้ามถ้า caller รายงานให้ผู้ใช้เห็นแล้ว (กัน toast ซ้ำ)
      if (!(error as any)?.__reported) {
        showAlert('เกิดข้อผิดพลาด', 'ไม่สามารถดำเนินการได้ กรุณาลองใหม่อีกครั้ง', 'error');
      }
    } finally {
      // คืนค่า loading เป็น false
      setConfirm(prev => ({ ...prev, isLoading: false }));
    }
  };

  return (
    <AlertContext.Provider value={{ showAlert, showConfirm }}>
      {children}
      
      {/* Alert Modal */}
      <AlertModal
        isOpen={alert.isOpen}
        onClose={closeAlert}
        title={alert.title}
        message={alert.message}
        type={alert.type}
      />
      
      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirm.isOpen}
        onClose={closeConfirm}
        onConfirm={handleConfirm}
        title={confirm.title}
        message={confirm.message}
        confirmText={confirm.confirmText}
        cancelText={confirm.cancelText}
        isDanger={confirm.isDanger}
        isLoading={confirm.isLoading}
      />
    </AlertContext.Provider>
  );
};

// Hook สำหรับใช้งาน Alert Context
export const useAlert = () => {
  const context = useContext(AlertContext);
  if (context === undefined) {
    throw new Error('useAlert must be used within an AlertProvider');
  }
  return context;
};

// Import toast functions
import { showAlert as showToastAlert, showToast } from './toast';

// Re-export จาก toast.tsx เพื่อความเข้ากันได้ย้อนหลัง
export const showAlert = showToastAlert;

/**
 * Error ที่ "รายงานให้ผู้ใช้เห็นแล้ว" (toast ถูกแสดงโดย mutation hook หรือ caller ไปแล้ว)
 * ใช้ throw จาก callback ของ showConfirm เพื่อบอกว่า action ล้มเหลว **โดยไม่ให้ handleConfirm
 * แสดง toast ซ้ำอีกใบ** — แก้ BUG-FE-TOAST-DUP (1 ความล้มเหลว → 2-3 toast ข้อความซ้ำ)
 */
export const reportedError = (message: string): Error => {
  const err = new Error(message) as Error & { __reported?: boolean };
  err.__reported = true;
  return err;
};

// BUG-S2275-001: กล่องยืนยันที่เปิดค้างอยู่ (key = หัวข้อ + ข้อความ) — ดับเบิลคลิกปุ่มลบเคยเปิด
// กล่องยืนยันซ้อน 2 กล่อง กดลบกล่องแรกสำเร็จ แล้วกล่องที่สองยิง DELETE ซ้ำได้ 404 "เกิดข้อผิดพลาด"
// ตามหลังข้อความสำเร็จ · กล่องเดิมยังเปิดอยู่ = คืน promise เดิม ไม่เปิดกล่องใหม่และไม่เรียก onConfirm ซ้ำ
const openConfirms = new Map<string, Promise<boolean>>();

// ฟังก์ชันสำหรับการใช้งานง่ายๆ
// onConfirm is optional - if not provided, just returns true/false based on user choice
export const showConfirm = (
  title: string,
  message: string,
  onConfirm?: () => void | Promise<void>,
  options = {}
): Promise<boolean> => {
  const key = `${title}\u0000${message}`;
  const pending = openConfirms.get(key);
  if (pending) return pending;
  const promise = openStandaloneConfirm(title, message, onConfirm, options);
  openConfirms.set(key, promise);
  promise.finally(() => openConfirms.delete(key));
  return promise;
};

const openStandaloneConfirm = (
  title: string,
  message: string,
  onConfirm?: () => void | Promise<void>,
  options = {}
): Promise<boolean> => {
  return new Promise((resolve) => {
    const confirmElement = document.createElement('div');
    confirmElement.id = 'standalone-confirm';
    document.body.appendChild(confirmElement);

    const ConfirmComponent = () => {
      const [isOpen, setIsOpen] = useState(true);
      const [isLoading, setIsLoading] = useState(false);

      const handleClose = () => {
        setIsOpen(false);
        resolve(false);
      };

      const handleConfirm = async () => {
        try {
          setIsLoading(true);
          // Only call onConfirm if it's provided
          if (onConfirm && typeof onConfirm === 'function') {
            await onConfirm();
          }
          setIsOpen(false);
          resolve(true);
        } catch (error: any) {
          console.error('Error in confirm action:', error);
          const errorMessage = error?.message || 'ไม่สามารถดำเนินการได้ กรุณาลองใหม่อีกครั้ง';
          setIsOpen(false);
          // ข้าม toast ถ้า caller รายงานให้ผู้ใช้เห็นแล้ว (กัน toast ซ้ำ — BUG-FE-TOAST-DUP)
          if (!error?.__reported) {
            setTimeout(() => {
              showAlert('เกิดข้อผิดพลาด', errorMessage, 'error');
            }, 300);
          }
          resolve(false);
        } finally {
          setIsLoading(false);
        }
      };

      useEffect(() => {
        if (!isOpen) {
          setTimeout(() => {
            // ถอดกล่องของตัวเอง (เดิมหา id ซ้ำ 'standalone-confirm' → ถ้ามีหลายกล่องจะถอดผิดกล่อง)
            if (confirmElement.parentNode) {
              confirmElement.parentNode.removeChild(confirmElement);
            }
          }, 300);
        }
      }, [isOpen]);

      const { confirmText, cancelText, isDanger } = options as any;

      return (
        <ConfirmModal
          isOpen={isOpen}
          onClose={handleClose}
          onConfirm={handleConfirm}
          title={title}
          message={message}
          confirmText={confirmText}
          cancelText={cancelText}
          isDanger={isDanger}
          isLoading={isLoading}
        />
      );
    };

    // Render แบบ standalone
    const root = ReactDOMClient.createRoot(confirmElement);
    root.render(<ConfirmComponent />);
  });
};

// Helper functions - ใช้ toast แทน modal
export const showSuccess = (message: string, title: string = 'สำเร็จ') => {
  showToast(title, message, 'success');
};

export const showError = (message: string, title: string = 'เกิดข้อผิดพลาด') => {
  showToast(title, message, 'error');
};

export const showWarning = (message: string, title: string = 'คำเตือน') => {
  showToast(title, message, 'warning');
};

export const showInfo = (message: string, title: string = 'ข้อมูล') => {
  showToast(title, message, 'info');
};

// ฟังก์ชันสำหรับแสดง Prompt Dialog (แทน window.prompt)
export const showPrompt = (
  title: string,
  message: string,
  options: {
    placeholder?: string;
    defaultValue?: string;
    submitText?: string;
    cancelText?: string;
    required?: boolean;
    inputType?: 'text' | 'textarea';
  } = {}
): Promise<string | null> => {
  return new Promise((resolve) => {
    const promptElement = document.createElement('div');
    promptElement.id = 'standalone-prompt';
    document.body.appendChild(promptElement);

    const PromptComponent = () => {
      const [isOpen, setIsOpen] = useState(true);
      const [isLoading, setIsLoading] = useState(false);

      const handleClose = () => {
        setIsOpen(false);
        resolve(null);
      };

      const handleSubmit = async (value: string) => {
        try {
          setIsLoading(true);
          setIsOpen(false);
          resolve(value);
        } catch (error) {
          console.error('Error in prompt action:', error);
          resolve(null);
        } finally {
          setIsLoading(false);
        }
      };

      useEffect(() => {
        if (!isOpen) {
          setTimeout(() => {
            const element = document.getElementById('standalone-prompt');
            if (element) {
              document.body.removeChild(element);
            }
          }, 300);
        }
      }, [isOpen]);

      return (
        <PromptModal
          isOpen={isOpen}
          onClose={handleClose}
          onSubmit={handleSubmit}
          title={title}
          message={message}
          placeholder={options.placeholder}
          defaultValue={options.defaultValue}
          submitText={options.submitText}
          cancelText={options.cancelText}
          required={options.required}
          isLoading={isLoading}
          inputType={options.inputType}
        />
      );
    };

    // Render แบบ standalone
    const root = ReactDOMClient.createRoot(promptElement);
    root.render(<PromptComponent />);
  });
};

// ตัวอย่างการใช้งาน:
// 1. ใช้งานผ่าน hook
// const { showAlert, showConfirm } = useAlert();
// showAlert('สำเร็จ', 'บันทึกข้อมูลสำเร็จ', 'success');
// showConfirm('ยืนยันการลบ', 'คุณต้องการลบข้อมูลนี้ใช่หรือไม่?', async () => { await deleteItem(); });

// 2. ใช้งานแบบ standalone
// import { showAlert, showConfirm, showSuccess, showError, showPrompt } from '../utils/alert';
// showSuccess('บันทึกข้อมูลสำเร็จ');
// showError('เกิดข้อผิดพลาดในการบันทึกข้อมูล');
// await showConfirm('ยืนยันการลบ', 'คุณต้องการลบข้อมูลนี้ใช่หรือไม่?', async () => { await deleteItem(); }, { isDanger: true });
// const reason = await showPrompt('เหตุผล', 'กรุณาระบุเหตุผล', { placeholder: 'ระบุเหตุผล...' });